import { cn } from '@/shared/lib/utils'
import { Switch } from '@/shared/ui/switch'
import { Label } from '@/shared/ui/label'
import { t } from '@/shared/lib/i18n'

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const
type WeekdayKey = (typeof WEEKDAY_KEYS)[number]

export const ALL_DAYS_ON: WeekdaySchedule = {
  sun: true,
  mon: true,
  tue: true,
  wed: true,
  thu: true,
  fri: true,
  sat: true,
}

/** Neutral placeholder while a parent preview reload is in flight (avoids ALL_DAYS flash). */
export const ALL_DAYS_OFF: WeekdaySchedule = {
  sun: false,
  mon: false,
  tue: false,
  wed: false,
  thu: false,
  fri: false,
  sat: false,
}

export interface WeekdaySchedule {
  sun: boolean
  mon: boolean
  tue: boolean
  wed: boolean
  thu: boolean
  fri: boolean
  sat: boolean
}

interface WeeklyScheduleGridProps {
  value: WeekdaySchedule
  onChange: (next: WeekdaySchedule) => void
  disabled?: boolean
  /** Required when showFollowParent is true (default) */
  followParent?: boolean
  /** Required when showFollowParent is true (default) */
  onFollowParentChange?: (checked: boolean) => void
  parentPreview?: WeekdaySchedule
  /**
   * When true with Follow Parent ON, avoids flashing a false full week while the
   * parent schedule is still loading (e.g. after department select → partial reload).
   */
  parentPreviewPending?: boolean
  /**
   * When true, parent preview reload failed after retries — `parentPreview` is a
   * neutral placeholder, not the trusted parent week.
   */
  parentPreviewFailed?: boolean
  /**
   * Retry parent-preview reload while Follow stays ON (preferred over Follow OFF→ON
   * for single-toggle surfaces; Departments also use this alongside multi-row nonce bumps).
   */
  onParentPreviewRetry?: () => void
  /** Required when showFollowParent is true (default) */
  parentLabel?: string
  idPrefix?: string
  /** When false, hides the Follow-Parent toggle row entirely (e.g. System-level schedule has no parent) */
  showFollowParent?: boolean
}

const DayCell = ({
  weekday,
  isOn,
  disabled,
  idPrefix,
  onToggle,
}: {
  weekday: WeekdayKey
  isOn: boolean
  disabled: boolean
  idPrefix: string
  onToggle?: (weekday: WeekdayKey, value: boolean) => void
}) => {
  const cellId = `${idPrefix}-${weekday}`
  return (
    <div className="flex flex-col items-center gap-1">
      <Label
        htmlFor={cellId}
        className={cn('text-xs font-medium', disabled && 'text-muted-foreground')}
      >
        {t(`schedule.days.${weekday}`)}
      </Label>
      <button
        id={cellId}
        type="button"
        disabled={disabled}
        onClick={() => onToggle?.(weekday, !isOn)}
        className={cn(
          'w-9 h-9 rounded text-xs font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          isOn
            ? 'bg-primary text-primary-foreground hover:bg-primary/90'
            : 'bg-muted text-muted-foreground hover:bg-muted/80',
          disabled && 'pointer-events-none opacity-60',
        )}
        aria-label={`${t(`schedule.days.${weekday}`)} ${isOn ? t('schedule.dayOn') : t('schedule.dayOff')}`}
        aria-pressed={isOn}
      >
        {isOn ? t('schedule.dayOn') : t('schedule.dayOff')}
      </button>
    </div>
  )
}

export const WeeklyScheduleGrid = ({
  value,
  onChange,
  disabled = false,
  followParent = false,
  onFollowParentChange,
  parentPreview,
  parentPreviewPending = false,
  parentPreviewFailed = false,
  onParentPreviewRetry,
  parentLabel,
  idPrefix = 'schedule',
  showFollowParent = true,
}: WeeklyScheduleGridProps) => {
  const handleToggle = (weekday: WeekdayKey, isOn: boolean) => {
    onChange({ ...value, [weekday]: isOn })
  }

  // Follow ON with no trusted preview: prefer OFF over ALL_DAYS_ON (UAT Sun–Sat class).
  const displaySchedule = followParent ? (parentPreview ?? ALL_DAYS_OFF) : value
  const switchId = `${idPrefix}-follow-parent`
  const parentPreviewHint = parentPreviewPending
    ? t('schedule.parentPreviewLoading')
    : parentPreviewFailed
      ? t('schedule.parentPreviewUnavailable')
      : t('schedule.parentPreviewHint')

  return (
    <div className="space-y-3">
      {showFollowParent && (
        <div className="flex items-center gap-2">
          <Switch
            id={switchId}
            checked={followParent}
            onCheckedChange={onFollowParentChange}
            disabled={disabled}
          />
          <Label htmlFor={switchId}>{parentLabel}</Label>
        </div>
      )}

      {showFollowParent && followParent && (
        <p className="text-xs text-muted-foreground">
          {parentPreviewHint}
          {parentPreviewFailed && onParentPreviewRetry && (
            <>
              {' '}
              <button
                type="button"
                className="underline underline-offset-2 hover:text-foreground"
                onClick={onParentPreviewRetry}
              >
                {t('schedule.parentPreviewRetry')}
              </button>
            </>
          )}
        </p>
      )}

      <div className={cn('flex flex-wrap gap-2', parentPreviewPending && 'opacity-60')}>
        {WEEKDAY_KEYS.map((weekday) => (
          <DayCell
            key={weekday}
            weekday={weekday}
            isOn={displaySchedule[weekday]}
            disabled={disabled || followParent}
            idPrefix={idPrefix}
            onToggle={followParent ? undefined : handleToggle}
          />
        ))}
      </div>
    </div>
  )
}
